import type { Theme } from "@emotion/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontAngkor, fontFredoka } from "@product/scmp-sdk";

import { ArticleImageContainer } from "scmp-app/components/article/article-image/styles";

type Props = {
  theme: Theme;
};

export const headlineStyle = (props: Props) => css`
  color: #ff7c1d;
  font-weight: 400;
  font-size: 32px;
  font-family: ${fontAngkor};
  line-height: 56px;
  letter-spacing: -0.64px;
  text-align: center;

  ${props.theme.breakpoints.up("tablet")} {
    font-size: 40px;
  }

  ${props.theme.breakpoints.up("desktop")} {
    font-size: 48px;
  }
`;

export const Heading6 = styled.h6`
  margin-block-end: 0;

  color: #ff7c1d;
  font-size: 20px;
  font-family: ${fontAngkor};
  line-height: 24px;
  letter-spacing: 0.4px;
  text-align: center;

  ${props => props.theme.breakpoints.up("tablet")} {
    font-size: 26px;
    line-height: 36px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 28px;
  }
`;

export const Content = styled.div`
  margin-block-start: 32px;
  margin-inline: auto;
  padding-block-end: 32px;
  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 486px;
    margin-block-start: 56px;
    padding-block-end: 56px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    max-inline-size: 632px;
    margin-block-start: 72px;
    padding-block-end: 72px;
  }
  ${props => props.theme.breakpoints.up("largeDesktop")} {
    max-inline-size: 644px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  div {
    margin-block-end: 24px;

    ${props => props.theme.breakpoints.up("desktop")} {
      margin-block-end: 32px;
    }
  }

  strong {
    font-weight: 600;
  }

  a {
    color: #333333;
    text-decoration: underline;
  }

  .image-inline-container {
    margin-block: 24px;

    ${props => props.theme.breakpoints.up("tablet")} {
      margin-block: 40px;
    }

    ${props => props.theme.breakpoints.up("desktop")} {
      margin-block: 48px;
    }
  }

  ${ArticleImageContainer} {
    ${props => props.theme.breakpoints.only("mobile")} {
      inset-inline: auto auto;

      inline-size: auto;
      margin-inline: auto;
    }

    img {
      inline-size: auto;
      max-inline-size: 100%;
      margin-inline: auto;
    }
  }

  ${Heading6} {
    margin-block-end: 0;

    ${props => props.theme.breakpoints.up("tablet")} {
      margin-block-end: 0;
    }

    ${props => props.theme.breakpoints.up("desktop")} {
      margin-block-end: 0;
    }
  }
`;

export const Headline = styled.h1`
  ${headlineStyle}
`;

export const Heading1 = styled.h1`
  ${headlineStyle}
`;

export const Heading2 = styled.h2`
  color: #ff7c1d;
  font-size: 32px;
  font-family: ${fontAngkor};
  line-height: 56px;
  letter-spacing: -0.64px;
  text-align: center;

  ${props => props.theme.breakpoints.up("tablet")} {
    font-size: 40px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 48px;
  }
`;

export const Heading3 = styled.h3`
  color: #ff7c1d;
  font-size: 20px;
  font-family: ${fontAngkor};
  line-height: 24px;
  letter-spacing: 0.4px;
  text-align: center;

  ${props => props.theme.breakpoints.up("tablet")} {
    font-size: 26px;
    line-height: 36px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 28px;
  }
`;

export const Heading4 = styled.h4`
  color: #ff7c1d;
  font-size: 16px;
  font-family: ${fontAngkor};
  line-height: 140%;
  letter-spacing: -0.16px;
  text-align: center;

  ${props => props.theme.breakpoints.up("tablet")} {
    font-size: 18px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 20px;
  }
`;

export const Paragraph = styled.div`
  color: #555555;
  font-size: 16px;
  font-family: ${fontFredoka};
  line-height: 140%;
  letter-spacing: -0.32px;
  text-align: center;

  strong {
    color: #333333;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    font-size: 18px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    font-size: 20px;
  }
`;
